import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Stack,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import download from 'downloadjs';
import { BsFillExclamationTriangleFill, FaFileDownload } from 'react-icons/all';
import { FiDownload } from 'react-icons/fi';
import { Button } from '~/components/Button';
import logo from '~/assets/logo-service-gray.png';
import { Table } from '~/components/Table';
import { AnnouncementsFile } from '~/types/announcements';
import { downloadFile } from '~/services/Announcements';
import { TextEditor } from '~/components/TextEditor/TextEditor';

interface ComunicadoDialogProps {
  status: number;
  exigirCiencia: boolean;
  setarStatusLido: () => void;
  onCloseModal: () => void;
  isOpenModal: boolean;
  motivoCancelamento?: string;
  dataPublicacao: string;
  titulo: string;
  mensagem: string;
  arquivos?: AnnouncementsFile[];
  isFetching?: boolean;
}

export const AnnouncementDialog: React.FC<ComunicadoDialogProps> = ({
  status,
  exigirCiencia,
  setarStatusLido,
  onCloseModal,
  isOpenModal,
  motivoCancelamento,
  dataPublicacao,
  titulo,
  mensagem,
  arquivos,
  isFetching,
}) => {
  const [cienteCliente, setCienteCliente] = useState(false);
  const toast = useToast();

  const handleCloseModal = () => {
    setCienteCliente(false);
    onCloseModal();
  };

  const salvar = () => {
    setCienteCliente(false);
    setarStatusLido();
  };

  const downloadArquivo = (id: string) => {
    downloadFile(id)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.message,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(() => {
        toast({
          title: 'Documento não encontrado',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  return (
    <AlertDialog
      closeOnEsc={false}
      closeOnOverlayClick={false}
      motionPreset="slideInBottom"
      leastDestructiveRef={undefined}
      onClose={status === 0 && !exigirCiencia ? salvar : handleCloseModal}
      isOpen={isOpenModal}
      isCentered
      size="2xl"
      scrollBehavior="inside"
    >
      <AlertDialogOverlay />
      <AlertDialogContent as="form">
        <AlertDialogHeader fontSize="xl" paddingBottom="10px" marginBottom={0}>
          <Text paddingBottom="10px">
            {status === 2 ? 'Comunicado cancelado' : 'Comunicado'}
          </Text>
          <hr />
        </AlertDialogHeader>
        {status !== 0 && <AlertDialogCloseButton />}
        {status === 0 && !exigirCiencia && <AlertDialogCloseButton />}
        <AlertDialogBody paddingTop={0}>
          {status === 2 && (
            <div>
              <div>
                <Icon
                  paddingLeft="5px"
                  as={BsFillExclamationTriangleFill}
                  fontSize={25}
                  style={{ color: 'orange' }}
                />
                <Text
                  fontWeight="bold"
                  paddingLeft="5px"
                  display="inline-block"
                >
                  Este comunicado foi cancelado pelo administrador.
                </Text>
              </div>
              <div>
                <Text
                  paddingLeft="5px"
                  fontSize="16px"
                  paddingTop={5}
                  paddingBottom="10px"
                >
                  {motivoCancelamento}
                </Text>
              </div>
              <hr />
            </div>
          )}
          <div
            style={{
              opacity: status === 2 ? '60%' : '100%',
              paddingTop: '20px',
            }}
          >
            <HStack justifyContent="space-between" alignItems="flex-end">
              <HStack>
                <Text fontWeight="bold">Data de publicação: </Text>
                <Text>{dataPublicacao}</Text>
              </HStack>
              <HStack>
                <img src={logo} alt="" width="100px" />
              </HStack>
            </HStack>
            <Stack py={3} gridGap={3}>
              <Text fontSize="24px" fontWeight="bold">
                {titulo}
              </Text>
              <TextEditor value={mensagem} visualizacao />
              {arquivos?.length && (
                <>
                  <Text fontWeight="bold" fontSize="14px">
                    Arquivos anexados:
                  </Text>
                  <TableContainer
                    overflowY="auto"
                    overflowX="unset"
                    maxHeight="152px"
                  >
                    <Table type="primary" border="1px solid #e5e5e6">
                      <Tbody>
                        {arquivos?.length &&
                          arquivos?.map(arquivo => (
                            <Tr key={arquivo.id}>
                              <Td style={{ paddingLeft: '10px' }}>
                                {arquivo.nome_arquivo}
                              </Td>
                              <Td textAlign="center">
                                <Tooltip
                                  hasArrow
                                  label="Download"
                                  bg="black"
                                  shouldWrapChildren
                                >
                                  <IconButton
                                    style={{ height: '1.8rem' }}
                                    onClick={() =>
                                      status !== 2 &&
                                      downloadArquivo(String(arquivo.id))
                                    }
                                    aria-label="Download"
                                    icon={<Icon as={FiDownload} />}
                                  />
                                </Tooltip>
                              </Td>
                            </Tr>
                          ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </>
              )}
              {status === 0 && exigirCiencia && (
                <Checkbox
                  colorScheme="b"
                  style={{ color: '#224dfe' }}
                  isChecked={cienteCliente}
                  onChange={() => {
                    setCienteCliente(!cienteCliente);
                  }}
                  fontSize="sm"
                >
                  <Text fontWeight="bold" fontSize="14px">
                    Li e estou ciente do comunicado
                  </Text>
                </Checkbox>
              )}
            </Stack>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter>
          {status !== 0 && (
            <Button onClick={handleCloseModal} variant="secondary">
              Fechar
            </Button>
          )}
          {status === 0 && !exigirCiencia && (
            <Button
              onClick={salvar}
              variant="secondary"
              isDisabled={isFetching}
            >
              Fechar {isFetching && <Spinner ml={2} />}
            </Button>
          )}
          {status === 0 && exigirCiencia && (
            <Button
              ml={3}
              onClick={salvar}
              isDisabled={!cienteCliente || isFetching}
            >
              Estou ciente {isFetching && <Spinner ml={2} />}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
